import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { formatUtcTimestampPrecise } from '~/utils/date';

import type { HistoryEvent } from '../../types/workflow-inspection.types';
import { humanizeEventType } from '../../utils/history-spans';
import { FailureChain } from '../failure-chain';
import { JsonViewer } from '../json-viewer';

/** Read a string, or `{ name }` / `{ kind }` object field, from event attributes. */
function attrText(
  attributes: Record<string, unknown>,
  key: string,
  sub?: 'name' | 'kind',
): string | null {
  const raw = attributes[key];
  if (raw === undefined || raw === null) return null;
  if (sub && typeof raw === 'object') {
    const inner = (raw as Record<string, unknown>)[sub];
    return typeof inner === 'string' ? inner : null;
  }
  return typeof raw === 'string' || typeof raw === 'number' ? String(raw) : null;
}

interface Props {
  event: HistoryEvent | null;
  onClose: () => void;
}

/**
 * Side drawer for one history event (Temporal's event detail): the headline
 * fields the contract calls out — Activity ID/Type, task queue + kind,
 * timeouts, attempt, identity, marker/patch — then the failure chain, the
 * activity input/result, and every attribute as JSON. Proto-JSON omits zero
 * fields, so every row is optional.
 */
export function EventDetailDrawer({ event, onClose }: Props) {
  const { t } = useTranslation('workflow-inspection');
  const attrs = event?.attributes ?? {};

  const rows: { label: string; value: string | null }[] = event
    ? [
        { label: t('drawer.activityId'), value: event.activityId ?? attrText(attrs, 'activityId') },
        {
          label: t('drawer.activityType'),
          value: event.activityType ?? attrText(attrs, 'activityType', 'name'),
        },
        { label: t('drawer.taskQueue'), value: attrText(attrs, 'taskQueue', 'name') },
        { label: t('drawer.taskQueueKind'), value: attrText(attrs, 'taskQueue', 'kind') },
        {
          label: t('drawer.scheduleToCloseTimeout'),
          value: attrText(attrs, 'scheduleToCloseTimeout'),
        },
        {
          label: t('drawer.scheduleToStartTimeout'),
          value: attrText(attrs, 'scheduleToStartTimeout'),
        },
        { label: t('drawer.startToCloseTimeout'), value: attrText(attrs, 'startToCloseTimeout') },
        { label: t('drawer.heartbeatTimeout'), value: attrText(attrs, 'heartbeatTimeout') },
        {
          label: t('drawer.attempt'),
          value: event.attempt !== null ? String(event.attempt) : null,
        },
        { label: t('drawer.identity'), value: event.identity },
        { label: t('drawer.markerName'), value: event.markerName },
        { label: t('drawer.patchId'), value: event.patchId },
        {
          label: t('drawer.scheduledEventId'),
          value:
            event.scheduledEventId !== null && event.scheduledEventId !== event.eventId
              ? String(event.scheduledEventId)
              : null,
        },
      ]
    : [];
  const input = attrs.input;
  const result = attrs.result;

  return (
    <Sheet
      open={event !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-2xl"
        data-testid="workflow-inspector-event-drawer"
      >
        {event && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-baseline gap-2">
                <span
                  className="text-muted-foreground font-mono text-base"
                  data-testid="workflow-inspector-event-drawer-event-id-value"
                >
                  {event.eventId}
                </span>
                {humanizeEventType(event.eventType)}
              </SheetTitle>
              <SheetDescription className="tabular-nums">
                {formatUtcTimestampPrecise(event.eventTime)}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-5 px-4 pb-6">
              {event.summary && <p className="text-foreground text-sm">{event.summary}</p>}

              <dl
                data-testid="workflow-inspector-event-drawer-fields"
                className="grid grid-cols-[minmax(140px,auto)_1fr] gap-x-6 gap-y-2 text-sm"
              >
                {rows
                  .filter((r) => r.value)
                  .map((r) => (
                    <Row key={r.label} label={r.label}>
                      {r.value}
                    </Row>
                  ))}
              </dl>

              {event.failure && <FailureChain failure={event.failure} />}
              {input !== undefined && (
                <Section title={t('drawer.input')}>
                  <JsonViewer
                    value={input}
                    title={t('drawer.input')}
                    testId="workflow-inspector-event-drawer-input-json"
                  />
                </Section>
              )}
              {result !== undefined && (
                <Section title={t('drawer.result')}>
                  <JsonViewer
                    value={result}
                    title={t('drawer.result')}
                    testId="workflow-inspector-event-drawer-result-json"
                  />
                </Section>
              )}
              <Section title={t('drawer.attributes')}>
                <JsonViewer
                  value={attrs}
                  title={t('drawer.attributes')}
                  initialDepth={1}
                  testId="workflow-inspector-event-drawer-attributes-json"
                />
              </Section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground font-mono text-xs break-all">{children}</dd>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}
